import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, errorMessage } from '../../shared/api/client';
import { Alert, Button, Card, EmptyState, ErrorState, Field, Loading } from '../../shared/ui/components';
import { useAsync } from '../../shared/ui/useAsync';
import { bibleLink, SavedCard } from '../bible/BiblePage';
import { useTabParam } from '../../shared/ui/useTabParam';
import { useMe } from '../session/SessionProvider';
import type { BiblePassage } from '../../shared/api/types';
import { NoteBody, passageBlock } from './noteMarkup';
import { NoteEditor, type NoteEditorHandle } from './NoteEditor';
import { PassagePicker } from './PassagePicker';

type Study = { id: string; title: string; body: string | null; passages: string[]; tags: string[]; updatedAt: string };

const SECTIONS = [
  ['estudios', 'Mis estudios'],
  ['notas', 'Notas de la Biblia'],
] as const;

/** Cuaderno de estudio: estudios propios con pasajes y etiquetas (privado). */
export function NotebookPage() {
  const data = useAsync((signal) => api.get<{ items: Study[] }>('/devotional/me/studies', signal).then((r) => r.items), []);
  const [section, setSection] = useTabParam(SECTIONS.map(([k]) => k));
  const [editing, setEditing] = useState<Study | 'new' | null>(null);
  const [tag, setTag] = useState('');
  const all = data.status === 'ready' ? data.data : [];
  const tags = [...new Set(all.flatMap((s) => s.tags))].sort();
  const shown = all.filter((s) => !tag || s.tags.includes(tag));
  const current = editing && editing !== 'new' ? editing : null;
  const editorRef = useRef<HTMLDivElement>(null);
  // En celular el editor se abre arriba de la lista: llevar la vista hasta él.
  useEffect(() => {
    if (editing) editorRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  }, [editing]);
  return (
    <div className="page page--narrow">
      <header className="page__header">
        <h1>Cuaderno de estudio</h1>
        <p className="muted">Tus estudios, notas y colecciones de pasajes. Solo tú los ves.</p>
      </header>
      <div className="tabs" role="tablist" aria-label="Cuaderno">
        {SECTIONS.map(([key, label]) => (
          <button key={key} type="button" role="tab" aria-selected={section === key} className={`tabs__tab${section === key ? ' tabs__tab--active' : ''}`} onClick={() => setSection(key)}>
            {label}
          </button>
        ))}
      </div>
      {section === 'notas' ? (
        <>
          <p className="small muted">
            Las notas que guardas al leer la <Link to="/app/biblia">Biblia</Link> («Guardar pasaje»). Aquí puedes abrirlas y editarlas.
          </p>
          <SavedCard version={0} title="Notas de la Biblia" />
        </>
      ) : null}
      {section !== 'estudios' ? null : !editing ? (
        <div>
          <Button onClick={() => setEditing('new')}>Nuevo estudio</Button>
        </div>
      ) : (
        <div ref={editorRef}>
          <StudyEditor key={current?.id ?? 'new'} study={current} onClose={(changed) => { setEditing(null); if (changed) data.reload(); }} />
        </div>
      )}
      {section === 'estudios' && tags.length ? (
        <div className="tabs tabs--scroll" role="tablist" aria-label="Etiquetas">
          {['', ...tags].map((t) => <button key={t || 'all'} type="button" role="tab" aria-selected={tag === t} className={`tabs__tab${tag === t ? ' tabs__tab--active' : ''}`} onClick={() => setTag(t)}>{t || 'Todo'}</button>)}
        </div>
      ) : null}
      {section === 'estudios' && data.status === 'loading' ? <Loading /> : null}
      {section === 'estudios' && data.status === 'error' ? <ErrorState error={data.error} onRetry={data.reload} /> : null}
      {section === 'estudios' && data.status === 'ready' && shown.length === 0 ? <Card><EmptyState title="Tu cuaderno está vacío." /></Card> : null}
      {(section === 'estudios' ? shown : []).map((s) => (
        <Card key={s.id} title={s.title} action={<Button variant="ghost" onClick={() => setEditing(s)}>Editar</Button>}>
          {s.passages.length ? <p className="plan__passages">{s.passages.map((p) => <Link key={p} to={bibleLink(p)}>{p}</Link>)}</p> : null}
          {s.body ? <NoteBody text={s.body} /> : null}
          {s.tags.length ? <p className="small muted">#{s.tags.join(' #')}</p> : null}
        </Card>
      ))}
    </div>
  );
}

/** Editor del estudio: inserta pasajes con su texto donde está el cursor, subraya, resalta y agrega notas. */
function StudyEditor({ study, onClose }: { study: Study | null; onClose: (changed: boolean) => void }) {
  const [body, setBody] = useState(study?.body ?? '');
  const [passages, setPassages] = useState<string[]>(study?.passages ?? []);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [fetching, setFetching] = useState(false);
  const editor = useRef<NoteEditorHandle>(null);
  async function insertPassage(ref: string) {
    setFetching(true);
    setMsg(null);
    try {
      const p = await api.get<BiblePassage>(`/bible/passage?ref=${encodeURIComponent(ref)}`);
      editor.current?.insertBlock(passageBlock(p.label, p.translation?.code ?? null, p.verses));
      setPassages((list) => (list.includes(p.label) || list.length >= 50 ? list : [...list, p.label]));
    } catch (err) {
      setMsg(errorMessage(err));
    } finally {
      setFetching(false);
    }
  }
  const save = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const payload = { title: String(f.get('title') ?? '').trim(), body: body || null, passages, tags: String(f.get('tags') ?? '').split(',').map((x) => x.trim()).filter(Boolean) };
    setBusy(true);
    setMsg(null);
    (study ? api.put(`/devotional/me/studies/${study.id}`, payload) : api.post('/devotional/me/studies', payload))
      .then(() => onClose(true))
      .catch((err) => setMsg(errorMessage(err)))
      .finally(() => setBusy(false));
  };
  return (
    <Card title={study ? 'Editar estudio' : 'Nuevo estudio'}>
      <form className="form" onSubmit={save}>
        {msg ? <Alert>{msg}</Alert> : null}
        <Field label="Título" name="title" required maxLength={150} defaultValue={study?.title ?? ''} />
        <PassagePicker onPick={(ref) => void insertPassage(ref)} busy={fetching} />
        <NoteEditor ref={editor} initial={study?.body ?? ''} onChange={setBody} onMessage={setMsg} />
        {passages.length ? (
          <div className="field">
            <strong className="block">Pasajes del estudio</strong>
            <p className="plan__passages">
              {passages.map((p) => (
                <span key={p} className="chip-inline">
                  {p}{' '}
                  <button type="button" className="link-button" aria-label={`Quitar ${p} de la lista`} onClick={() => setPassages((l) => l.filter((x) => x !== p))}>×</button>
                </span>
              ))}
            </p>
          </div>
        ) : null}
        <Field label="Etiquetas (separadas por coma)" name="tags" defaultValue={study?.tags.join(', ') ?? ''} placeholder="fe, oración" />
        <div className="actions">
          <Button type="submit" busy={busy}>Guardar</Button>
          <Button variant="ghost" onClick={() => onClose(false)}>Cancelar</Button>
          {study ? <Button variant="ghost" busy={busy} onClick={() => { if (window.confirm('¿Borrar este estudio?')) void api.delete(`/devotional/me/studies/${study.id}`).then(() => onClose(true)); }}>Borrar</Button> : null}
        </div>
      </form>
    </Card>
  );
}

type Comment = { id: string; kind: string; body: string; hidden: boolean; createdAt: string; mine: boolean; author: { personId: string; name: string } };
const KIND: Record<string, string> = { comentario: 'Comentario', pregunta: 'Pregunta', reflexion: 'Reflexión', oracion: 'Oración' };

/** Conversación de un día del plan (de toda la iglesia o de un grupo de lectura). */
export function DayConversation({ planId, day, groupId = null }: { planId: string; day: number; groupId?: string | null }) {
  const me = useMe();
  const url = `/devotional/churches/${me.activeChurchId}/plans/${planId}/days/${day}/comments`;
  const [open, setOpen] = useState(false);
  const data = useAsync((signal) => (open ? api.get<{ canModerate: boolean; items: Comment[] }>(`${url}${groupId ? `?group=${groupId}` : ''}`, signal) : Promise.resolve(null)), [url, open, groupId]);
  const [error, setError] = useState<string | null>(null);
  if (!open) return <Button variant="ghost" onClick={() => setOpen(true)}>Conversación</Button>;
  const d = data.status === 'ready' ? data.data : null;
  return (
    <div className="conversation">
      {error ? <Alert>{error}</Alert> : null}
      {data.status === 'loading' ? <Loading /> : null}
      {d ? (
        <ul className="list">
          {d.items.length === 0 ? <li className="small muted">Nadie ha comentado este día. ¡Sé el primero!</li> : null}
          {d.items.map((c) => (
            <li key={c.id} className="list__item list__item--stack">
              <span className="small muted">{KIND[c.kind]} · {c.author.name}{c.hidden ? ' · oculto' : ''}</span>
              <span className="pre-line">{c.body}</span>
              {c.mine || d.canModerate ? <Button variant="ghost" onClick={() => void api.delete(`/devotional/churches/${me.activeChurchId}/plan-comments/${c.id}`).then(() => data.reload()).catch((e) => setError(errorMessage(e)))}>{c.mine ? 'Borrar' : 'Ocultar'}</Button> : null}
            </li>
          ))}
        </ul>
      ) : null}
      <form className="family-add" onSubmit={(e) => {
        e.preventDefault();
        const form = e.currentTarget;
        const f = new FormData(form);
        void api.post(url, { groupId, kind: String(f.get('kind')), body: String(f.get('body') ?? '').trim() }).then(() => { form.reset(); data.reload(); }).catch((err) => setError(errorMessage(err)));
      }}>
        <div className="field"><label htmlFor={`ck-${planId}-${day}`}>Tipo</label><select id={`ck-${planId}-${day}`} name="kind">{Object.entries(KIND).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
        <Field label="Escribe" name="body" required maxLength={2000} />
        <Button type="submit" variant="secondary">Enviar</Button>
      </form>
    </div>
  );
}
